import {
  ICellRendererParams,
  ValueFormatterParams,
} from '@ag-grid-community/core'
import { Money } from '../../models/workbench-row.model'
import { RowOrigin, TARGET_META } from '../../models/source-key.model'

const AMOUNT_DIGITS: Intl.NumberFormatOptions = {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
}

/**
 * Currency symbols come from the platform's own `Intl.NumberFormat`, not from an icon font.
 *
 * An icon set was the alternative — the repo already ships `@mdi/font`, which has `mdi-currency-*`
 * glyphs — but it is the wrong tool here. MDI covers roughly a dozen currencies and the Enfusion
 * feed already returns NOK, and a glyph is not text: it would not survive copy/paste, CSV export or
 * the set filter, and it cannot be right-sized against the digits beside it. `Intl` adds nothing to
 * the bundle, knows every ISO-4217 code, and emits real characters.
 *
 * `currencyDisplay: 'symbol'` (rather than `narrowSymbol`) is deliberate: it keeps CA$ and A$
 * distinct from $, which narrowSymbol collapses.
 *
 * Constructing a formatter is expensive relative to a cell render, so one is cached per currency —
 * `null` marks a code the platform rejected, so a bad code is not retried on every row.
 */
const CURRENCY_FORMATTERS = new Map<string, Intl.NumberFormat | null>()

const currencyFormatter = (currency: string): Intl.NumberFormat | null => {
  const cached = CURRENCY_FORMATTERS.get(currency)
  if (cached !== undefined) return cached

  let formatter: Intl.NumberFormat | null = null
  try {
    formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      currencyDisplay: 'symbol',
      ...AMOUNT_DIGITS,
    })
  } catch {
    // A malformed code (anything but three letters) makes the constructor throw.
    formatter = null
  }

  CURRENCY_FORMATTERS.set(currency, formatter)
  return formatter
}

/**
 * ICU separates a bare currency code from its amount with a non-breaking space (U+00A0). It is
 * swapped for an ordinary space so the rendered text matches what a user typing into the set
 * filter — or reading a copied cell — would expect.
 */
const withPlainSpaces = (text: string): string => text.replace(/\u00a0/g, ' ')

export const isMoney = (value: unknown): value is Money =>
  typeof value === 'object' &&
  value !== null &&
  'value' in value &&
  'currency' in value &&
  typeof (value as Money).value === 'number'

/** Format a bare amount — no currency — using the same digits and negative style as money. */
export const formatAmount = (value: number): string => {
  const magnitude = Math.abs(value).toLocaleString('en-US', AMOUNT_DIGITS)
  return value < 0 ? `(${magnitude})` : magnitude
}

export const formatMoney = (money: Money | null | undefined): string => {
  if (!money || money.value == null) return '—'

  const magnitude = Math.abs(money.value)
  const formatter = currencyFormatter(money.currency)
  const amount = formatter
    ? withPlainSpaces(formatter.format(magnitude))
    : `${money.currency} ${magnitude.toLocaleString('en-US', AMOUNT_DIGITS)}`

  return money.value < 0 ? `(${amount})` : amount
}

export const moneyValueFormatter = (params: ValueFormatterParams): string => {
  if (isMoney(params.value)) return formatMoney(params.value)
  // The API omits `currency` on quantity-like amounts (notionalQuantity, servicesTradingNotional…),
  // so a column catalogued as MONEY can still receive a bare number. Show it rather than blanking
  // the cell — only a genuinely non-numeric value has nothing to render.
  if (typeof params.value === 'number') return formatAmount(params.value)
  return '—'
}

export const moneyComparator = (a: unknown, b: unknown): number => {
  const av = isMoney(a) ? a.value : Number.NEGATIVE_INFINITY
  const bv = isMoney(b) ? b.value : Number.NEGATIVE_INFINITY
  return av - bv
}

export const formatDate = (value: unknown): string => {
  if (value == null || value === '') return '—'
  const date = new Date(value as string)
  if (isNaN(date.getTime())) return String(value)
  const mm = `${date.getMonth() + 1}`.padStart(2, '0')
  const dd = `${date.getDate()}`.padStart(2, '0')
  return `${mm}/${dd}/${date.getFullYear()}`
}

export const formatDateTime = (value: unknown): string => {
  if (value == null || value === '') return '—'
  const date = new Date(value as string)
  if (isNaN(date.getTime())) return String(value)
  return `${formatDate(value)} ${date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })}`
}

export const formatBoolean = (value: unknown): string =>
  value == null ? '—' : value ? 'Yes' : 'No'

const swatch = (icon: string, color: string, count: number): string =>
  `<span style="display:inline-flex;align-items:center;gap:4px">` +
  `<i class="mdi ${icon}" style="color:${color};font-size:15px"></i>` +
  `<span style="font-weight:700;color:${color}">${count}</span></span>`

export const breaksCellRenderer = (params: ICellRendererParams): string => {
  const breaks = params.data?.breaks
  if (!breaks || (breaks.high === 0 && breaks.medium === 0)) {
    return `<span style="color:#9ca3af;opacity:.5">—</span>`
  }
  const parts: string[] = []
  if (breaks.high > 0)
    parts.push(swatch('mdi-alert-circle', '#db1f30', breaks.high))
  if (breaks.medium > 0)
    parts.push(swatch('mdi-alert', '#e0a516', breaks.medium))
  return `<span style="display:inline-flex;align-items:center;gap:10px">${parts.join(
    '',
  )}</span>`
}

const STATUS_STYLE: Record<string, { label: string; color: string }> = {
  matched: { label: 'MATCHED', color: '#028d53' },
  unmatched: { label: 'UNMATCHED', color: '#b70406' },
  ignored: { label: 'IGNORED', color: '#52607d' },
  // Hold is a sub-state of unmatched in recon, so it reads as a caution rather than a resolution.
  // This map being `Record<string, …>` is why the entry is mandatory: an unmapped status renders a
  // blank cell instead of failing to compile.
  held: { label: 'HELD', color: '#b45309' },
}

export const statusCellRenderer = (params: ICellRendererParams): string => {
  const style = STATUS_STYLE[String(params.value)]
  if (!style) return ''
  return `<span style="font-weight:700;letter-spacing:.3px;color:${style.color}">${style.label}</span>`
}

const ORIGIN_COLOR: Record<RowOrigin, string> = {
  api: '#7d5fff',
  cust: '#217dfe',
  trad: '#e48727',
  cw: '#0047d1',
}

export const sourceCellRenderer = (params: ICellRendererParams): string => {
  const value = String(params.value ?? '')
  if (!value) return ''
  const color = params.data
    ? ORIGIN_COLOR[params.data.origin as RowOrigin]
    : '#52607d'
  return `<span style="font-weight:700;letter-spacing:.3px;color:${color}">${value.toUpperCase()}</span>`
}

/** Build a per-target match-count renderer coloured by the target source. */
export const makeMatchCountRenderer =
  (color: string) =>
  (params: ICellRendererParams): string => {
    const count = Number(params.value ?? 0)
    if (!count) {
      return `<span style="color:#9ca3af;opacity:.5"><i class="mdi mdi-minus"></i></span>`
    }
    return (
      `<span style="display:inline-flex;align-items:center;gap:4px;cursor:pointer;color:${color}">` +
      `<i class="mdi mdi-link"></i><span style="font-weight:700">${count}</span></span>`
    )
  }

export const matchTargetColor = (target: keyof typeof TARGET_META): string =>
  TARGET_META[target].color

export const commentsCellRenderer = (params: ICellRendererParams): string => {
  const count = Number(params.value ?? 0)
  const color = count > 0 ? '#1162e7' : '#9ca3af'
  const opacity = count > 0 ? '1' : '.4'
  return (
    `<span style="display:inline-flex;align-items:center;gap:4px;color:${color};opacity:${opacity}">` +
    `<i class="mdi mdi-comment-outline"></i><span style="font-weight:700">${count}</span></span>`
  )
}
